import { BrowserProvider, Contract, Signer } from "ethers";
import {
  KEYSERVER_DOMAIN,
  KEYSERVER_URL,
  SEPOLIA_PUBLIC_COLLECTION,
} from "./Client";

export interface Chain {
  mint: (metadata_url: string) => Promise<BigInt>;
  readMetadataUrl: (collection: string, tokenid: BigInt) => Promise<string>;
  // We may need to generalize this in the future (instead of always returning EIP5361 data).
  // For now this is sufficient, since we only have Ethereum chains.
  auth: () => Promise<{ message: string; sig: string }>;
  ownerOf: (collection: string, tokenid: BigInt) => Promise<string>;
  waitFinalized: () => WaitFinalized;
}

interface WaitFinalized {
  estimate: Date;
  promise: Promise<void>;
}

// See https://docs.soliditylang.org/en/v0.5.3/abi-spec.html#function-selector-and-argument-encoding
const OWNER_OF_METHOD_ERC721 = "0x6352211e"; // ownerOf(uint256)

// TODO: Can we somehow merge this with SepoliaChain?
export class SepoliaChainReadOnly implements Chain {
  private provider: BrowserProvider;

  constructor(provider: BrowserProvider) {
    this.provider = provider;
  }

  public async mint(metadata_url: string): Promise<BigInt> {
    throw "ReadOnly";
  }

  public async readMetadataUrl(
    collection: string,
    tokenid: BigInt,
  ): Promise<string> {
    const abi = ["function tokenURI(uint256) view returns(string)"];
    const contract = new Contract(collection, abi, this.provider);
    return await contract.tokenURI(tokenid);
  }
  public auth(): Promise<{ message: string; sig: string }> {
    throw "ReadOnly";
  }
  public async ownerOf(collection: string, tokenid: BigInt): Promise<string> {
    const data = OWNER_OF_METHOD_ERC721 + uint256Hex(tokenid);
    const owner = await this.provider.call({ data, to: collection });
    // Remove padding bytes
    return `0x${owner.slice(26)}`;
  }

  public waitFinalized(): WaitFinalized {
    throw "Not implemented";
  }
}

export class SepoliaChain implements Chain {
  private signer: Signer;

  constructor(signer: Signer) {
    this.signer = signer;
  }

  public async mint(metadata_url: string): Promise<BigInt> {
    const abi = [
      "function mint(string)",
      "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)",
    ];
    const contract = new Contract(SEPOLIA_PUBLIC_COLLECTION, abi, this.signer);
    const tx = await contract.mint(metadata_url);
    console.log(tx);
    const receipt = await tx.wait(1);
    console.log(receipt);
    // NOTE: If we support other contracts at some point: Those may emit other events.
    if (receipt.logs.length !== 1 || receipt.logs[0].args.length !== 3) {
      throw Error("Contract did not emit Transfer event");
    }
    return receipt.logs[0].args[2];
  }

  public async readMetadataUrl(
    collection: string,
    tokenid: BigInt,
  ): Promise<string> {
    const abi = ["function tokenURI(uint256) view returns(string)"];
    const contract = new Contract(collection, abi, this.signer);
    return await contract.tokenURI(tokenid);
  }

  public async auth() {
    const address = await this.signer.getAddress();
    const chain = 1337;
    const nonce = 32891756; // TODO: Randomize
    const issued_at = "2021-09-30T16:25:24Z"; // TODO: Read current timestamp into this format

    const message = `${KEYSERVER_DOMAIN} wants you to sign in with your Ethereum account:
${address}

URI: ${KEYSERVER_URL}/tee-login
Version: 1
Chain ID: ${chain}
Nonce: ${nonce}
Issued At: ${issued_at}`;

    const sig = await this.signer.signMessage(message);

    return { message, sig };
  }

  // TODO: Merge this duplicate logic with the read-only chain?
  public async ownerOf(collection: string, tokenid: BigInt): Promise<string> {
    const data = OWNER_OF_METHOD_ERC721 + uint256Hex(tokenid);
    const owner = await this.signer.provider?.call({ data, to: collection });
    if (owner) {
      // Remove padding bytes
      return `0x${owner.slice(26)}`;
    } else {
      return "";
    }
  }

  // TODO: Implement this properly. This timespan is larger than needed in
  // most cases and can be too short if the chain is under load/attack.
  public waitFinalized(): WaitFinalized {
    // 3 Epochs with 32 slots with 12 seconds
    const duration = 3 * 32 * 12 * 1000;
    const estimate = new Date(Date.now() + duration);
    const promise = new Promise<void>((resolve) => {
      setTimeout(() => resolve(), duration);
    });
    return { estimate, promise };
  }
}

function uint256Hex(value: BigInt): string {
  return value.toString(16).padStart(64, "0");
}
